import { useId, type ReactNode, type TextareaHTMLAttributes } from 'react'

import { CONTROL_BASE, CONTROL_INTERACTIVE, CONTROL_INVALID } from '@/components/ui/control'
import { cn } from '@/lib/cn'

/* Form controls. Field errors sit BESIDE the field; record errors sit above the
 * record. Every control is labelled, because
 * full keyboard operability and labelled form controls are the accessibility
 * floor here, not a nice-to-have.
 *
 * The controls themselves now live one per file — Input, Select, DatePicker,
 * Checkbox, Radio — because each is a real component rather than a styled
 * native element. They are re-exported at the bottom so the eight call sites
 * that already import `{ Field, Select }` from here keep working, and so there
 * is still one obvious place to look for "the form controls".
 */

export interface FieldProps {
  label: string
  /** Sits beside the field, not in a summary at the top. */
  error?: string
  hint?: ReactNode
  required?: boolean
  children: (props: {
    id: string
    'aria-describedby'?: string
    'aria-invalid'?: boolean
  }) => ReactNode
  className?: string
}

/* Base UI (`@base-ui/react`): `Field` considered and rejected. Base UI wires ids and validity
   through `Field.Control`, which must wrap the input itself; this Field is a
   render prop that hands `{id, aria-describedby, aria-invalid}` to an arbitrary
   child, including ones that are not inputs at all. */
export function Field({
  label,
  error,
  hint,
  required,
  children,
  className,
}: FieldProps) {
  const id = useId()
  const describedBy = error ? `${id}-error` : hint ? `${id}-hint` : undefined

  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <label
        htmlFor={id}
        className="text-text-body text-[12.5px] leading-none font-normal"
      >
        {label}
        {required ? (
          <span className="text-negative ml-0.5" aria-hidden>
            *
          </span>
        ) : null}
      </label>

      {/* `aria-invalid` is passed down with the id and the description, so a
          control renders its error border from the same source that renders the
          message — the two cannot disagree, and no call site has to remember to
          wire it. */}
      {children({
        id,
        'aria-describedby': describedBy,
        'aria-invalid': error ? true : undefined,
      })}

      {error ? (
        <p id={`${id}-error`} className="text-negative text-[12px] leading-snug">
          {error}
        </p>
      ) : hint ? (
        <p
          id={`${id}-hint`}
          className="text-text-muted text-[12px] leading-snug font-light"
        >
          {hint}
        </p>
      ) : null}
    </div>
  )
}

/** Multi-line text. The one control that stays a bare native element: a
 *  `<textarea>` has no OS-drawn popup to replace, and its resize handle is a
 *  genuine affordance for the long-form entity footers it holds. */
export function Textarea({
  className,
  rows = 3,
  ...props
}: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return (
    <textarea
      rows={rows}
      className={cn(
        CONTROL_BASE,
        CONTROL_INTERACTIVE,
        CONTROL_INVALID,
        'focus:border-brand focus:bg-surface focus:shadow-none focus:outline-none',
        'resize-y px-3 py-2 leading-normal',
        className,
      )}
      {...props}
    />
  )
}

/* Re-exported so `import { Field, Select, Checkbox } from '@/components/ui/Field'`
   keeps resolving. New code may import from either path. */
export { Checkbox } from '@/components/ui/Checkbox'
export type { CheckboxProps } from '@/components/ui/Checkbox'
export { DatePicker } from '@/components/ui/DatePicker'
export type { DatePickerProps } from '@/components/ui/DatePicker'
export { Input } from '@/components/ui/Input'
export type { InputProps } from '@/components/ui/Input'
export { Radio, RadioGroup } from '@/components/ui/Radio'
export type { RadioGroupProps, RadioOption, RadioProps } from '@/components/ui/Radio'
export { Select } from '@/components/ui/Select'
export type { SelectOption, SelectProps } from '@/components/ui/Select'

/* `toOptions` is deliberately NOT re-exported here. It is a plain function, and
   a module that exports both components and functions loses Vite fast refresh
   for every component in it. Import it from '@/components/ui/control'. */
